/* eslint-disable no-unused-vars */
import React, { useState } from "react";
import { Form, Select, Button, Spin, Table, Tag, message, Input, Modal } from "antd";
import { ExclamationCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../../api";
import urls from "../../api/urls";
import back_img from "../../assests/images/rightVector.svg";
import Smcs_Loader from "../../assests/images/loader-new.gif";

const { Option } = Select;
const { TextArea } = Input;

// Parse comma/newline/space-separated AWBs into a clean array
const parseAwbs = (raw) =>
  raw
    .split(/[\n,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const resultColumns = [
  {
    title: "AWB Number",
    dataIndex: "awbNumber",
    key: "awbNumber",
  },
  {
    title: "Result",
    dataIndex: "result",
    key: "result",
    render: (result) =>
      result === "SUCCESS" ? (
        <Tag color="green" icon={<CheckCircleOutlined />}>Updated</Tag>
      ) : (
        <Tag color="red" icon={<CloseCircleOutlined />}>Failed</Tag>
      ),
  },
  {
    title: "Reason",
    dataIndex: "reason",
    key: "reason",
    render: (reason) => reason || "—",
  },
];

function MarkLostDamage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [resultData, setResultData] = useState([]);

  const submitToApi = async (values, awbNumbers) => {
    const userInfo = JSON.parse(localStorage.getItem("userId") || "{}");
    const premiseId = userInfo?.userInfo?.premiseId;
    const userId = userInfo?.userInfo?.userId;

    setLoading(true);
    try {
      const payload = {
        awbNumbers,
        status: values.status,
        premiseId,
        userId,
        ...(values.remark ? { remark: values.remark.trim() } : {}),
      };

      const response = await API.Api(urls.MARK_SHIPMENT_LOST_DAMAGE).postIdHeader(payload);
      const resData = response?.response?.data;
      const payloadStatus = resData?.status;

      if (payloadStatus === 200 || payloadStatus === 207) {
        const successSet = new Set(resData?.successfulAwbs || []);
        const failureMap = {};
        (resData?.failures || []).forEach((f) => {
          failureMap[f.awbNumber] = f.reason;
        });

        const rows = awbNumbers.map((awb, idx) => ({
          key: idx,
          awbNumber: awb,
          result: successSet.has(awb) ? "SUCCESS" : "FAILED",
          reason: failureMap[awb] || null,
        }));

        setResultData(rows);

        if (payloadStatus === 200) {
          message.success(
            `All ${awbNumbers.length} shipment(s) marked as ${values.status.toLowerCase()} successfully.`
          );
        } else {
          // Friendly 207 message
          const sc = resData?.successCount ?? 0;
          const fc = resData?.failureCount ?? 0;
          const statusLabel = values.status.toLowerCase();
          if (sc > 0 && fc > 0) {
            message.warning({
              content: (
                <span>
                  <strong>{sc} shipment(s)</strong> marked as {statusLabel},{" "}
                  <strong>{fc} shipment(s)</strong> could not be updated. Check the results table for details.
                </span>
              ),
              duration: 6,
            });
          } else if (sc === 0) {
            message.error({
              content: (
                <span>
                  None of the {fc} shipment(s) could be marked as {statusLabel}. Check the results table for reasons.
                </span>
              ),
              duration: 6,
            });
          }
        }
        form.resetFields();
      } else {
        message.error(
          resData?.message || "Failed to update shipment status. Please try again."
        );
      }
    } catch {
      message.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onFinish = (values) => {
    const awbNumbers = parseAwbs(values.awbNumbers);
    if (awbNumbers.length === 0) {
      message.warning("Please enter at least one AWB number.");
      return;
    }

    const statusLabel = values.status === "LOST" ? "Lost" : "Damaged";

    Modal.confirm({
      title: `Mark shipment(s) as ${statusLabel}?`,
      icon: <ExclamationCircleOutlined style={{ color: values.status === "LOST" ? "#ff4d4f" : "#fa8c16" }} />,
      content: (
        <div style={{ paddingTop: 4 }}>
          <p style={{ margin: "0 0 8px", color: "#595959" }}>
            You are about to mark <strong>{awbNumbers.length} shipment(s)</strong> as{" "}
            <strong style={{ color: values.status === "LOST" ? "#ff4d4f" : "#fa8c16" }}>
              {statusLabel}
            </strong>. This action cannot be undone.
          </p>
          {values.remark && (
            <p style={{ margin: 0, color: "#8c8c8c", fontSize: 13 }}>
              Remark: <em>{values.remark}</em>
            </p>
          )}
        </div>
      ),
      okText: `Yes, Mark as ${statusLabel}`,
      okButtonProps: {
        danger: values.status === "LOST",
        style: values.status === "DAMAGED" ? { background: "#fa8c16", borderColor: "#fa8c16" } : {},
      },
      cancelText: "Cancel",
      onOk: () => submitToApi(values, awbNumbers),
    });
  };

  return (
    <div style={{ padding: "20px", background: "#F8FAFC", minHeight: "100vh" }}>
      <div
        style={{
          background: "#fff",
          padding: "16px 24px",
          borderRadius: "8px",
          boxShadow: "0 1px 2px rgba(16, 24, 40, 0.05)",
        }}
      >
        {/* Header */}
        <div style={{ marginBottom: "24px", display: "flex", alignItems: "center" }}>
          <img
            src={back_img}
            onClick={() => navigate(-1)}
            style={{ cursor: "pointer", marginRight: "8px" }}
            alt="back"
          />
          <h1 style={{ margin: 0, fontSize: "20px", fontWeight: "600", color: "#101828" }}>
            Mark Lost / Damaged
          </h1>
        </div>

        {/* Form */}
        <Spin
          spinning={loading}
          indicator={
            <img src={Smcs_Loader} style={{ height: "80px", width: "80px" }} alt="Loading..." />
          }
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            className="global-form"
            style={{ maxWidth: 520 }}
          >
            <Form.Item
              label="AWB Number(s)"
              name="awbNumbers"
              extra="Enter one or multiple AWB numbers, separated by comma, space, or new line."
              rules={[{ required: true, message: "Please enter at least one AWB number" }]}
            >
              <TextArea
                placeholder={"e.g. 18066495615298\n18066495615299, 18066495615300"}
                rows={4}
                style={{ borderRadius: "8px", fontFamily: "monospace" }}
              />
            </Form.Item>

            <Form.Item
              label="Status"
              name="status"
              rules={[{ required: true, message: "Please select a status" }]}
            >
              <Select
                placeholder="Select status"
                size="large"
                style={{ borderRadius: "8px" }}
              >
                <Option value="LOST">Lost</Option>
                <Option value="DAMAGED">Damaged</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Remark"
              name="remark"
            >
              <TextArea
                placeholder="Optional — reason for marking as lost/damaged"
                rows={2}
                maxLength={255}
                showCount
                style={{ borderRadius: "8px" }}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={loading}
                style={{
                  background: "#175EB6",
                  borderRadius: "8px",
                  fontWeight: "600",
                  paddingInline: 32,
                }}
              >
                Submit
              </Button>
            </Form.Item>
          </Form>
        </Spin>

        {/* Results */}
        {resultData.length > 0 && (
          <div style={{ marginTop: "24px" }}>
            <h2
              style={{
                fontSize: "16px",
                fontWeight: "600",
                color: "#101828",
                marginBottom: "12px",
              }}
            >
              Results
            </h2>
            <Table
              columns={resultColumns}
              dataSource={resultData}
              pagination={false}
              size="middle"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default MarkLostDamage;
